import { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { gql, useMutation, useQuery } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { mdiDeleteOutline, mdiLoading } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";
import dayjs from "dayjs";
import { Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { useToggle } from "usehooks-ts";
import * as Yup from "yup";

import Button from "components/Button";
import ConfirmationModal from "components/ConfirmationModal";
import {
  FR_CONTRACT_TYPE,
  FR_LINE_TYPE_LIST,
  FR_STATUS,
  PERCENTAGE_LIST
} from "constants/haulpay-broker";
import { CARRIER_PAYMENT_SPEED } from "constants/haulpay-carrier";
import { routes } from "constants/routes";
import { EQUIPMENT_TYPES } from "constants/signup";
import { FR_DISABLE_EDIT_STATUS } from "constants/status";
import { MUTATION_UPDATE_CARRIER_FUNDING_REQUEST } from "features/main/HaulPay/Carrier/RequestPay/query";
import { MUTATION_UPDATE_BROKER_FUNDING_REQUEST } from "features/main/Loads/Post/FundingRequest/query";
import { parseError } from "helpers/convertFormData";
import { showErrorMessage, showSuccessMessage, showWarningMessage } from "helpers/toastMessage";
import useDeviceType from "hooks/useDeviceType";
import { useUserFeature } from "hooks/useUserFeature";
import { IOption } from "models/Form";
import {
  ICompany,
  IFRAttachment,
  IFundingRequest,
  IFundingRequestLineItems,
  ILineList,
  IPaymentProfile
} from "models/HaulPay";
import { AddressInfo } from "models/User";
import { deleteFundingRequestAPI, sendInvoiceAPI } from "services/api/haulpay";
import StepOne from "../StepOne";
import StepThree from "../StepThree";
import StepTwo from "../StepTwo";
import FRStatus from "./FRStatus";
import { QUERY_BROKER_FUNDING_REQUEST, QUERY_CARRIER_FUNDING_REQUEST } from "./query";
import styles from "./style.module.scss";
import "./_style.scss";
import formatBankAccount from "helpers/formatBankAccount";
import { useBrokerContext } from "context/BrokerContext";
import { useModal } from "context/ModalContext";
import LoadingSpinner from "components/LoadingSpinner";
import pick from "common/pick";
import { FUNDING_REQUEST_CONTRACT_TYPES } from "helpers/constantTypes";
import { useSelector } from "react-redux";
import { selectFactoringProfile } from "../../../../../../store/user/selector";
import { formatNumber } from "../../../../../../helpers/formatNumber";

interface IProps {
  companyValue?: ICompany;
  setIsShowSearch?: (show: boolean) => void;
  setCompanyValue?: (data: ICompany) => void;
}

export interface IEditFundingRequest extends IFundingRequest {
  carrierEmail?: string;
  carrierPhone?: string;
  carrierPaymentMethod?: string;
  carrierMc?: string;
  debtorEmail?: string;
}

const fundingRequestBrokerFormSchema = (
  allowNegativeMargin: boolean,
  negativeMarginValue: number,
  negativeMarginLimit: number
) =>
  Yup.object<Partial<IFundingRequest>>({
    shipperInvoiceAmount: Yup.number()
      .required("This field is required")
      .typeError("This field is required")
      .transform((val, orig) => (orig === "" ? undefined : val)),

    carrierInvoiceAmount: Yup.number()
      .required("This field is required")
      .typeError("This field is required")
      .test(
        "carrier-invoice-amount",
        "", // we'll supply a custom message via createError()
        function (value) {
          const { shipperInvoiceAmount } = this.parent as IFundingRequest;

          // let required/typeError handle missing or non-numeric
          if (typeof value !== "number" || typeof shipperInvoiceAmount !== "number") {
            return true;
          }

          // Determine whether we should allow a negative margin
          const useNegMargin = allowNegativeMargin && negativeMarginLimit > 0;

          // If negative margin is allowed, the ceiling is shipperInvoiceAmount + negativeMarginValue
          // Otherwise, ceiling is simply shipperInvoiceAmount
          const ceiling = useNegMargin
            ? shipperInvoiceAmount + negativeMarginValue
            : shipperInvoiceAmount;
          if (value > ceiling) {
            const msg = useNegMargin
              ? `Negative margin cannot exceed ${formatNumber(negativeMarginValue, 2)}`
              : `Carrier invoice amount must be less than or equal to ${formatNumber(
                  shipperInvoiceAmount,
                  2
                )}`;

            return this.createError({ message: msg });
          }

          return true;
        }
      )
      .transform((val, orig) => (orig === "" ? undefined : val))
  });

const fundingRequestCarrierFormSchema = (status: string) =>
  Yup.object<Partial<IFundingRequest>>({
    shipperInvoiceAmount: Yup.number()
      .typeError("This field is required")
      .required("This field is required"),
    billToCompanyAddress: Yup.object<AddressInfo>().when([], {
      is: () => status !== "Incomplete",
      then: (schema) => schema.required("This field is required"),
      otherwise: (schema) => schema.notRequired()
    }),
    billToCompanyPhone: Yup.string().when([], {
      is: () => status !== "Incomplete",
      then: (schema) => schema.required("This field is required"),
      otherwise: (schema) => schema.notRequired()
    })
  });

export default function ManageFundingRequest({
  companyValue,
  setIsShowSearch,
  setCompanyValue
}: Readonly<IProps>) {
  let startY = 0;
  const { fundingRequestId } = useParams();
  const navigate = useNavigate();
  const { isDesktop } = useDeviceType();
  const { isBroker, isCarrier, isNonFactorRefactorEnabled, isAutoGenerateLoadNumbersEnabled } =
    useUserFeature();
  const { openModal, closeModal } = useModal();
  const [fundingRequestValue, setFundingRequestValue] = useState("factored");
  const [selectedFR, setSelectedFR] = useState<any>();
  const [attachments, setAttachments] = useState<IFRAttachment[]>([]);
  const [isGlobalDisabled, setIsGlobalDisabled] = useState(false);
  const [selectedPaymentProfile, setSelectedPaymentProfile] = useState<IPaymentProfile>();
  const [isFactoringCompany, setIsFactoringCompany] = useState(false);
  const [isFetchingCalculation, setFetchingCalculation] = useState(false);
  const [isOpen, toggleAccordion] = useToggle(false);
  const [isSendInvoiceConfirmModalOpen, toggleSendInvoiceConfirmModal] = useToggle();
  const [isDeleteConfirmModalOpen, toggleDeleteConfirmModal] = useToggle();

  const [editFR, setEditFR] = useToggle();
  const { frCalculation, calculateFR } = useBrokerContext();
  const carrierCanEdit = (isCarrier && editFR) || isBroker;
  const factoringProfile = useSelector(selectFactoringProfile);
  const allowNegativeMargin = factoringProfile?.allow_negative_margin ?? false;
  const negativeMarginValue = factoringProfile?.negative_margin_value / 100 ?? 0;
  const negativeMarginLimit = factoringProfile?.negative_margin_limit ?? 0;

  const validationSchema = useMemo(() => {
    if (isBroker) {
      return fundingRequestBrokerFormSchema(
        allowNegativeMargin,
        negativeMarginValue,
        negativeMarginLimit
      );
    } else if (selectedFR?.status) {
      return fundingRequestCarrierFormSchema(selectedFR.status);
    } else return Yup.object<Partial<IFundingRequest>>({});
  }, [isBroker, selectedFR, allowNegativeMargin, negativeMarginValue, negativeMarginLimit]);
  const methods = useForm<Partial<IEditFundingRequest>>({
    resolver: yupResolver(validationSchema)
  });
  const initialValuesRef = useRef<Partial<IEditFundingRequest> | null>(null);
  const {
    reset,
    control,
    getValues,
    setValue,
    formState: { isDirty, isSubmitting },
    handleSubmit
  } = methods;

  const { data, loading, error } = useQuery(
    isBroker ? QUERY_BROKER_FUNDING_REQUEST : QUERY_CARRIER_FUNDING_REQUEST,
    {
      variables: {
        id: fundingRequestId
      },
      fetchPolicy: "cache-and-network"
    }
  );
  useEffect(() => {
    if (!loading && error) {
      showErrorMessage(error.message);
    }
  }, [loading, error]);

  const [updateBrokerFR] = useMutation(MUTATION_UPDATE_BROKER_FUNDING_REQUEST, {
    onError(err) {
      err.graphQLErrors.map(({ message }) => showErrorMessage(parseError(message)));
    },
    onCompleted(data) {
      const updatedFundingRequest = data.createOrUpdateBrokerFundingRequest?.fundingRequest;
      setSelectedFR(updatedFundingRequest);
    }
  });

  const [updateCarrierFR] = useMutation(MUTATION_UPDATE_CARRIER_FUNDING_REQUEST, {
    onError(err) {
      err.graphQLErrors.map(({ message }) => showErrorMessage(parseError(message)));
    }
  });

  const amount = useWatch({ control: control, name: "shipperInvoiceAmount" });
  const contractType = useWatch({ control: control, name: "fundingRequestType" });
  const paymentProfileAmount = useWatch({ control: control, name: "carrierInvoiceAmount" });
  const payoutDays = useWatch({ control: control, name: "carrierPaymentSpeed" });
  const factoringFeeSplitRate = useWatch({ control: control, name: "brokersQRFee" });
  const isMarginAdvance = useWatch({ control: control, name: "marginAdvance" });

  const sendInvoice = async () => {
    try {
      const res = await sendInvoiceAPI(fundingRequestId as any);
      toggleSendInvoiceConfirmModal();
      toggleSendInvoiceConfirmModal();
      if (res.data?.email_not_verified) {
        showWarningMessage(
          "This debtor's email has not been verified yet so an invoice cannot be sent at this time. We will verify it in 1-2 business days. If your debtor is set to receive invoices from us automatically then they will still receive their invoice once the funding request is approved even if you don't come back to send it from here later."
        );
      } else {
        showSuccessMessage("Invoice sent successfully.");
      }
    } catch {
      showErrorMessage("Failed to send invoice.");
    }
  };

  const deleteFR = async () => {
    try {
      await deleteFundingRequestAPI(fundingRequestId as any);
      toggleDeleteConfirmModal();
      showSuccessMessage("Funding request deleted successfully.");
      navigate(isBroker ? routes.HAULPAY_BROKER_MANAGE_PAY : routes.HAULPAY_CARRIER_MANAGE_PAY);
    } catch {
      showErrorMessage("Failed to delete funding request.");
    }
  };

  const updateBtnLabel = (): JSX.Element | string => {
    if (isSubmitting) {
      return <Icon path={mdiLoading} size={1} spin={1} />;
    } else if (isDesktop) {
      return "Update";
    } else {
      return "UPDATE";
    }
  };

  const disabledFields = useMemo(() => {
    if (selectedFR) {
      switch (selectedFR.status) {
        case "Approved":
        case "Paid":
        case "Closed":
          return true;
      }
    }
  }, [selectedFR]);

  useEffect(() => {
    if (data) {
      setSelectedFR(data.fundingRequest);
      setIsGlobalDisabled(FR_DISABLE_EDIT_STATUS.includes(data.fundingRequest.status));
    }
  }, [data]);

  const carrierPaymentMethod = (status: string, paymentMethod: any, factoringCompany: any) => {
    if (factoringCompany?.id) {
      setIsFactoringCompany(true);
    }
    if (status !== "Paid" && !!factoringCompany?.id) {
      return `${factoringCompany?.name.toUpperCase()} ${formatBankAccount(
        factoringCompany?.account?.accountNumberRedacted,
        factoringCompany?.account?.method
      )}`;
    }
    return factoringCompany?.name &&
      factoringCompany?.account.accountNumberRedacted === paymentMethod?.accountNumberRedacted
      ? `${factoringCompany?.name.toUpperCase()} ${formatBankAccount(
          paymentMethod?.accountNumberRedacted,
          paymentMethod?.method
        )}`
      : formatBankAccount(paymentMethod?.accountNumberRedacted, paymentMethod?.method);
  };

  const getPaymentMethod = (paymentMethod: any, factoringCompany: any) => {
    return factoringCompany?.name ? factoringCompany?.account?.method : paymentMethod?.method;
  };

  const formattedData = useMemo(() => {
    if (data) {
      const fundingRequestTypeObject = FR_CONTRACT_TYPE.find(
        ({ value }) => data.fundingRequest.contractType === value
      );
      const brokersQRFeeObject = PERCENTAGE_LIST.find(
        ({ value }) => value === data.fundingRequest.factoringFeeSplitRate?.split(".")[0]
      );
      const carrierPaymentSpeedObject = CARRIER_PAYMENT_SPEED.find(
        ({ value }) => value === data?.fundingRequest?.payoutDays
      );
      return {
        fundingRequestTypeObject,
        brokersQRFeeObject,
        carrierPaymentSpeedObject
      };
    }
    return null;
  }, [data]);

  const mapCategoryStringToOption = (category: string): IOption | null => {
    return (
      FR_LINE_TYPE_LIST.find((option) => option.value.toLowerCase() === category.toLowerCase()) ||
      null
    );
  };

  const onTouchStartSliderContainer = (e: React.TouchEvent<HTMLDivElement>) => {
    startY = e.touches[0].clientY;
  };

  const onTouchEndSliderContainer = (e: React.TouchEvent<HTMLDivElement>) => {
    const endY = e.changedTouches[0].clientY;
    const distance = endY - startY;
    if (distance < -20 && isOpen) {
      toggleAccordion();
    }
  };

  useEffect(() => {
    if (selectedFR && formattedData) {
      const equipmentType = EQUIPMENT_TYPES.find(
        (item) => item.value === selectedFR.lastUsedLoadTrailerType
      );
      const initialValues = {
        paymentProfileDisplay: getPaymentMethod(
          selectedFR.paymentProfileAccount,
          selectedFR.factoringCompany
        ),
        carrierPaymentMethod: carrierPaymentMethod(
          selectedFR.status,
          selectedFR.paymentProfileAccount,
          selectedFR.factoringCompany
        ),
        carrierEmail: selectedFR.paymentProfile?.ownerEmail,
        carrierMc: selectedFR.paymentProfile?.companyMc,
        carrierPhone: selectedFR.paymentProfile?.companyPhoneNumber,
        debtor: selectedFR.debtor,
        paymentProfile: selectedFR.paymentProfile?.companyName,
        invoiceNumber: selectedFR.invoiceNumber,
        userLoadNumber: selectedFR.userLoadNumber,
        fundingRequestType: formattedData.fundingRequestTypeObject ?? FR_CONTRACT_TYPE[0],
        shipperInvoiceAmount: parseFloat(selectedFR.amount.toFixed(2)),
        carrierInvoiceAmount: parseFloat(selectedFR.paymentProfileAmount.toFixed(2)),
        carrierRate: isBroker
          ? selectedFR.paymentProfileDiscountRate + "%"
          : Math.floor(selectedFR.discountRate) + "%",
        brokersQRFee: formattedData.brokersQRFeeObject ?? PERCENTAGE_LIST[0],
        carrierPaymentSpeed: formattedData.carrierPaymentSpeedObject ?? CARRIER_PAYMENT_SPEED[0],
        carrierPro: selectedFR.carrierProNumber,
        PONumber: selectedFR.purchaseOrderNumber,
        userNotes: selectedFR.userNotes,
        clientInvoiceNumber: selectedFR.userReferenceNumber,
        carrierInvoiceDetails: selectedFR.carrierInvoiceNotes,
        debtorInvoiceDetails: selectedFR.debtorInvoiceNotes,
        firstPickUp: selectedFR.firstOriginLocation,
        finalDelivery: selectedFR.finalDestinationLocation,
        billToCompany: selectedFR.debtor,
        billToCompanyAddress: selectedFR.billToAddress,
        billToCompanyPhone: selectedFR.billToCompanyPhone,
        createdAt: selectedFR.created ? dayjs(selectedFR.created).format("MM/DD/YYYY") : "",
        loadLength: selectedFR.loadLength,
        equipmentType: equipmentType ?? EQUIPMENT_TYPES[0],
        expectedProcessingAt: selectedFR.scheduledPurchaseDate
          ? dayjs(selectedFR.scheduledPurchaseDate).format("MM/DD/YYYY")
          : "",
        carrierFundedAt: selectedFR.fundedAt
          ? dayjs(selectedFR.fundedAt).format("MM/DD/YYYY hh:mm A")
          : "",
        invoiceDate: selectedFR.invoiceDate
          ? dayjs(selectedFR.invoiceDate).format("MM/DD/YYYY")
          : "",
        brokerFundedAt: selectedFR.brokerFundedAt
          ? dayjs(selectedFR.brokerFundedAt).format("MM/DD/YYYY hh:mm A")
          : "",
        amountFunded: selectedFR.amountFunded,
        fundingRequestStatus:
          FR_STATUS.find(
            ({ value }) => value === selectedFR.contractType && selectedFR.status === "Non Factored"
          ) ?? FR_STATUS.find(({ label }) => label === selectedFR.status),
        statusNote: selectedFR.statusNote,
        lineItems: (selectedFR.frLineItems?.map((item: ILineList) => {
          const mappedCategory = mapCategoryStringToOption(item.category);
          if (!mappedCategory) {
            return {
              amount: parseFloat(Math.floor(item.amount / 100).toFixed(2)),
              category: { id: 999, label: "Unknown", value: "Unknown" }
            };
          }
          return {
            amount: parseFloat(Math.floor(item.amount / 100).toFixed(2)),
            category: mappedCategory
          };
        }) || []) as IFundingRequestLineItems[],
        marginAdvance: selectedFR.applyReserveHoldback,
        attachments: selectedFR.attachments
      };
      reset(initialValues);
      initialValuesRef.current = initialValues;
      setAttachments(selectedFR.attachments);
      setCompanyValue &&
        setCompanyValue({
          ...selectedFR.debtor,
          dba: selectedFR.debtor?.name
        });
    }
  }, [selectedFR, formattedData]);

  const memoizedValues = useMemo(() => {
    if (!frCalculation) return null;

    const {
      reserve_amount,
      factoring_payable,
      payment_profile_invoice_amount,
      factoring_fee,
      invoice_amount,
      payment_profile_fee,
      payment_profile_discount_rate,
      factoring_fee_split_rate,
      payment_profile_payable
    } = frCalculation;
    return {
      reserveHoldBack: parseFloat((Math.floor(reserve_amount) / 100).toFixed(2)),
      brokerGrossMargin: parseFloat(
        (Math.floor(invoice_amount - payment_profile_invoice_amount) / 100).toFixed(2)
      ),
      carrierPay: parseFloat((Math.floor(payment_profile_payable) / 100).toFixed(2)),
      yourFee: parseFloat((Math.floor(factoring_fee) / 100).toFixed(2)),
      brokerPay: parseFloat((Math.floor(factoring_payable) / 100).toFixed(2)),
      carrierFee: parseFloat((Math.floor(payment_profile_fee) / 100).toFixed(2)),
      carrierRate: payment_profile_discount_rate + "%",
      brokersQRFee: PERCENTAGE_LIST.find(({ amount }) => amount === factoring_fee_split_rate)
    };
  }, [frCalculation]);

  useEffect(() => {
    async function calculateFRAPI() {
      try {
        setFetchingCalculation(true);
        await calculateFR({
          apply_reserve_holdback: isMarginAdvance,
          debtor: selectedFR?.debtor.id,
          factored: !getValues("fundingRequestType.value").startsWith("NON_FACTORED"),
          invoice_amount: Number(getValues("shipperInvoiceAmount") ?? 0) * 100,
          bill_out_only: getValues("fundingRequestType.value") === "NON_FACTORED_BILL_OUT",
          contract_type: getValues("fundingRequestType.value"),
          create_rates_revision: false,
          factoring_fee_split_rate: Number(factoringFeeSplitRate?.value ?? 0),
          payment_profile: selectedFR?.paymentProfile?.id,
          payment_profile_amount: 0,
          payment_profile_invoice_amount: Number(getValues("carrierInvoiceAmount") ?? 0) * 100,
          payment_speed: parseInt(payoutDays?.value?.match(/\d+/g)?.[0] ?? "0", 10),
          self_finance_requested: getValues("fundingRequestType.value").startsWith("SELF_FINANCE")
        });
      } catch {
        return null;
      } finally {
        setFetchingCalculation(false);
      }
    }

    if (amount && payoutDays?.value && !isFetchingCalculation) {
      calculateFRAPI();
    }
  }, [
    amount,
    contractType?.value,
    paymentProfileAmount,
    isMarginAdvance,
    payoutDays?.value,
    factoringFeeSplitRate?.value
  ]);

  useEffect(() => {
    if (memoizedValues) {
      const {
        reserveHoldBack,
        brokerGrossMargin,
        carrierPay,
        yourFee,
        brokerPay,
        carrierFee,
        brokersQRFee,
        carrierRate
      } = memoizedValues;

      if (reserveHoldBack !== getValues("reserveHoldBack")) {
        setValue("reserveHoldBack", reserveHoldBack);
      }
      if (brokerGrossMargin !== getValues("brokerGrossMargin")) {
        setValue("brokerGrossMargin", brokerGrossMargin);
      }
      if (carrierPay !== getValues("carrierPay")) {
        setValue("carrierPay", carrierPay);
      }
      if (yourFee !== getValues("yourFee")) {
        setValue("yourFee", yourFee);
      }
      if (yourFee !== getValues("yourFee")) {
        setValue("yourFee", yourFee);
      }
      if (brokerPay !== getValues("brokerPay")) {
        setValue("brokerPay", brokerPay);
      }
      if (carrierFee !== getValues("carrierFee")) {
        setValue("carrierFee", carrierFee);
      }
      if (brokersQRFee !== getValues("brokersQRFee")) {
        setValue("brokersQRFee", brokersQRFee);
      }
      if (carrierRate !== getValues("carrierRate")) {
        setValue("carrierRate", carrierRate);
      }
    }
  }, [memoizedValues, setValue, getValues]);

  const VALIDATE_FUNDING_REQUESTS = gql`
    mutation ValidateFundingRequests(
      $request: BrokerFundingRequestInput
      $contractType: String!
      $status: String!
    ) {
      validateFundingRequests(request: $request, contractType: $contractType, status: $status) {
        results {
          id
          valid
          errors
          warnings
        }
      }
    }
  `;

  const [validateFRs] = useMutation(VALIDATE_FUNDING_REQUESTS, {
    onError: (err) => {
      if (err?.graphQLErrors)
        return err?.graphQLErrors.forEach(({ message }) =>
          showErrorMessage(parseError(message), {
            autoClose: 100000
          })
        );
      if (Array.isArray(err))
        return err.forEach(
          (message) => showErrorMessage(<div dangerouslySetInnerHTML={{ __html: message }} />),
          { autoClose: 100000 }
        );
    },
    onCompleted: (data) => {
      const hasErrors = data.validateFundingRequests.results.some((r: any) => !r?.valid);
      if (hasErrors) {
        throw data.validateFundingRequests.results
          .filter((r: any) => !r?.valid)
          .flatMap((r: any) => r.errors.map((err: string) => err));
      }
    }
  });

  const onSubmit = async (data: IFundingRequest) => {
    try {
      let res;
      let variables = {};
      const originLocations = {
        streetOne: data?.firstPickUp?.address ?? data?.firstPickUp?.street_one,
        city: data?.firstPickUp?.city ?? "",
        state: data?.firstPickUp?.state ?? "",
        zip: data?.firstPickUp?.zip ?? "",
        country: data?.firstPickUp?.country ?? ""
      };
      const destinationLocations = {
        streetOne: data?.finalDelivery?.address ?? data?.finalDelivery?.street_one,
        city: data?.finalDelivery?.city ?? "",
        state: data?.finalDelivery?.state ?? "",
        zip: data?.finalDelivery?.zip ?? "",
        country: data?.finalDelivery?.country ?? ""
      };

      if (isBroker) {
        const frLineList = (data?.lineItems ?? []).map((item) => ({
          amount: item.amount ? Number(item.amount * 100) : 0,
          category: item.category.value
        })) as ILineList[];

        let metadata = {};
        if (
          isNonFactorRefactorEnabled &&
          selectedFR?.metadata?.previous_payout_days &&
          (([
            FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_STANDARD,
            FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_BILL_OUT
          ].includes(selectedFR.contractType) &&
            data?.fundingRequestType?.value === FUNDING_REQUEST_CONTRACT_TYPES.STD_BROKER) ||
            ([
              FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_STANDARD,
              FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_BILL_OUT
            ].includes(data?.fundingRequestType?.value) &&
              selectedFR.contractType === FUNDING_REQUEST_CONTRACT_TYPES.STD_BROKER))
        ) {
          metadata = {
            paymentProfileId: selectedFR?.metadata?.previous_payment_profile_id,
            factoringFeeSplitRate: selectedFR?.metadata?.previous_discount_rate,
            paymentProfileAmount: selectedFR?.metadata?.previous_payment_profile_amount,
            payoutDays:
              selectedFR?.metadata?.previous_payout_days === 0
                ? "ach_1_day"
                : `ach_${selectedFR?.metadata?.previous_payout_days}_day`
          };
        }
        variables = {
          id: fundingRequestId,
          amount: Number(data.shipperInvoiceAmount * 100),
          applyReserveHoldback: isMarginAdvance,
          debtor: data.debtor.id,
          paymentProfileAmount: Number(data.carrierInvoiceAmount * 100),
          userLoadNumber: data.userLoadNumber,
          loadLength: selectedFR.loadLength,
          paymentProfilePaymentMethod: (data?.paymentProfileDisplay ?? "ach").toLowerCase(),
          paymentProfileId: selectedPaymentProfile?.id ?? selectedFR.paymentProfile?.id,
          // factoringAmount: data.brokerGrossMargin * 100,
          factoringFeeSplitRate: data.brokersQRFee.value,
          payoutDays: data.carrierPaymentSpeed.value,
          lineItems: frLineList,
          contractType: data.fundingRequestType.value,
          carrierProNumber: data.carrierPro,
          purchaseOrderNumber: data.PONumber,
          status: data.fundingRequestStatus.value,
          originLocations: originLocations,
          destinationLocations: destinationLocations,
          attachments:
            data.attachments?.map((att) =>
              pick(att, ["visibleTo", "filename", "id", "category"])
            ) || [],
          userNotes: data.userNotes,
          userReferenceNumber: data.clientInvoiceNumber,
          debtorInvoiceDetails: data.debtorInvoiceDetails,
          carrierInvoiceDetails: data.carrierInvoiceDetails,
          statusNote: data.statusNote,
          ...metadata
        };

        const {
          attachments,
          debtorInvoiceDetails,
          carrierInvoiceDetails,
          status,
          contractType,
          ...rest
        } = variables as any;

        const validationRequest = {
          ...rest,
          files: attachments || [],
          debtorInvoiceNotes: debtorInvoiceDetails || "",
          carrierInvoiceNotes: carrierInvoiceDetails || "",
          originLocations: [originLocations],
          destinationLocations: [destinationLocations]
        };

        const validationResponse = await validateFRs({
          variables: {
            request: validationRequest,
            contractType,
            status: status.toUpperCase().replace(/\s+/g, "_")
          }
        });
        if (validationResponse?.errors?.length) return;
        const hasWarnings = validationResponse.data.validateFundingRequests.results.some(
          (r: any) => r?.warnings?.length > 0
        );
        if (hasWarnings) {
          const warnings = validationResponse.data.validateFundingRequests.results.flatMap(
            (r: any) => r.warnings.map((warning: string) => warning)
          );
          for (const warning of warnings) {
            await new Promise((resolve, reject) =>
              openModal("warning", {
                message: warning,
                onConfirm: () => {
                  resolve(true);
                  closeModal("warning");
                },
                onClose: () => {
                  reject("cancel-edit-fr");
                  closeModal("warning");
                },
                confirmText: "Submit",
                closeText: "Close"
              })
            );
          }
        }
        if (
          (selectedPaymentProfile || data?.paymentProfile) &&
          data.fundingRequestStatus.value === "Remote Approved" &&
          ![
            FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_BILL_OUT,
            FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_STANDARD
          ].includes(data?.fundingRequestType?.value)
        ) {
          const payoutDays =
            selectedPaymentProfile?.payout_days ??
            selectedFR?.paymentProfile?.brokerPaymentProfileRelation?.payoutDays;
          const brokersQRFee =
            selectedPaymentProfile?.factoring_fee_split_rate ??
            selectedFR?.paymentProfile?.brokerPaymentProfileRelation?.factoringFeeSplitRate;

          if (
            payoutDays !== data?.carrierPaymentSpeed.value ||
            Number(brokersQRFee) !== Number(data?.brokersQRFee?.value)
          ) {
            await new Promise((resolve, reject) =>
              openModal("confirmation-fr-payment-speed", {
                confirmButton: () => {
                  resolve(true);
                  closeModal("confirmation-fr-payment-speed");
                },
                onCloseModal: () => reject("cancel-edit-fr"),
                defaultValue: {
                  payout_days: payoutDays,
                  factoring_fee_split_rate: brokersQRFee
                },
                currentValue: {
                  payout_days: data?.carrierPaymentSpeed.value,
                  factoring_fee_split_rate: data?.brokersQRFee?.value
                },
                initialValue: {
                  factoring_fee_split_rate: initialValuesRef.current?.brokersQRFee?.value,
                  payout_days: initialValuesRef.current?.carrierPaymentSpeed?.value
                }
              })
            );
          }
        }
        if (isBroker) {
          res = await updateBrokerFR({
            variables: variables
          });
        }
      } else {
        res = await updateCarrierFR({
          variables: {
            id: fundingRequestId,
            amount: Number(data?.shipperInvoiceAmount * 100),
            debtor: data?.billToCompany?.id ?? "",
            userLoadNumber: data.userLoadNumber ?? "",
            billToCompanyPhone: data?.billToCompanyPhone ?? "",
            billToCompanyEmail: data?.billToCompany?.email ?? "",
            streetOne:
              data?.billToCompanyAddress?.address ?? data?.billToCompanyAddress?.street_one,
            city: data?.billToCompanyAddress?.city ?? "",
            state: data?.billToCompanyAddress?.state ?? "",
            zip: data?.billToCompanyAddress?.zip ?? "",
            country: data?.billToCompanyAddress?.country ?? "",
            fuelAdvanceAmount: Number(data.shipperInvoiceAmount * 100),
            originLocations: originLocations,
            destinationLocations: destinationLocations
          }
        });
      }
      if (!res?.errors) {
        showSuccessMessage("Successfully edited transaction");
      }
    } catch (e) {
      if (e === "cancel-edit-fr") {
        return;
      }
      showErrorMessage("Failed to edit transaction");
    }
  };
  return (
    <FormProvider {...methods}>
      <LoadingSpinner isFetching={loading} />
      <form className="mobile:flex mobile:flex-col mobile:h-full" onSubmit={handleSubmit(onSubmit)}>
        <FRStatus
          isOpen={isOpen}
          toggleAccordion={toggleAccordion}
          selectedFR={selectedFR}
          frCalculation={frCalculation}
        />
        {!isDesktop ? (
          <div
            className={styles.swiper}
            onTouchStart={onTouchStartSliderContainer}
            onTouchEnd={onTouchEndSliderContainer}>
            <Swiper
              modules={[Pagination]}
              autoHeight={true}
              centeredSlides={true}
              spaceBetween={10}
              slidesPerView={1.08}
              pagination={{ clickable: true }}
              className="mobile:h-[calc(100vh-296px)] swiper-funding-request mobile:!overflow-y-auto">
              <SwiperSlide>
                <StepOne
                  isGlobalDisabled={isGlobalDisabled}
                  setFundingRequestValue={setFundingRequestValue}
                  carrierCanEdit={carrierCanEdit}
                  companyValue={companyValue}
                  isFactoringCompany={isFactoringCompany}
                />
              </SwiperSlide>
              <SwiperSlide>
                <StepTwo
                  isGlobalDisabled={isGlobalDisabled}
                  fundingRequestValue={fundingRequestValue}
                  selectedPaymentProfile={selectedPaymentProfile}
                  setSelectedPaymentProfile={setSelectedPaymentProfile}
                  companyValue={companyValue}
                  setIsShowSearch={setIsShowSearch}
                  isFactoringCompany={isFactoringCompany}
                  carrierCanEdit={carrierCanEdit}
                />
              </SwiperSlide>
              <SwiperSlide>
                <StepThree
                  isGlobalDisabled={isGlobalDisabled}
                  attachments={attachments}
                  invoiceSentCount={selectedFR?.invoiceSentCount}
                  carrierCanEdit={carrierCanEdit}
                />
              </SwiperSlide>
            </Swiper>
          </div>
        ) : (
          <div className="mt-5">
            <StepOne
              isGlobalDisabled={isGlobalDisabled}
              setFundingRequestValue={setFundingRequestValue}
              factoringCompany={selectedFR?.factoringCompany}
              carrierCanEdit={carrierCanEdit}
              companyValue={companyValue}
              isFactoringCompany={isFactoringCompany}
              isAutoGenerateLoadNumbersEnabled={isAutoGenerateLoadNumbersEnabled}
            />
            <StepTwo
              isGlobalDisabled={isGlobalDisabled}
              fundingRequestValue={fundingRequestValue}
              companyValue={companyValue}
              selectedPaymentProfile={selectedPaymentProfile}
              setSelectedPaymentProfile={setSelectedPaymentProfile}
              setIsShowSearch={setIsShowSearch}
              isFactoringCompany={isFactoringCompany}
              carrierCanEdit={carrierCanEdit}
            />
            <StepThree
              isGlobalDisabled={isGlobalDisabled}
              attachments={attachments}
              invoiceSentCount={selectedFR?.invoiceSentCount}
              carrierCanEdit={carrierCanEdit}
            />
          </div>
        )}

        <div className={styles.footer}>
          {isBroker ? (
            <>
              <Button
                type="button"
                rounded
                variant="outline"
                onClick={toggleDeleteConfirmModal}
                className="!text-error !border-error !p-3 !size-14 !rounded-full">
                <Icon path={mdiDeleteOutline} className="!size-6" />
              </Button>
              <Button
                type="button"
                variant="outline"
                className={styles.sendButton}
                onClick={toggleSendInvoiceConfirmModal}
                disabled={isDirty}>
                {!isDesktop ? "SEND INVOICE" : "Send Invoice"}
              </Button>
            </>
          ) : null}
          {carrierCanEdit ? (
            <Button
              type="submit"
              className={clsx(styles.updateButton, !isBroker ? "!ml-auto" : "")}
              disabled={!carrierCanEdit || disabledFields}>
              {updateBtnLabel()}
            </Button>
          ) : (
            <Button
              onClick={(e) => {
                e.preventDefault();
                setEditFR();
              }}
              className={clsx(
                styles.updateButton,
                "!bg-blue-500 !text-white",
                !isBroker ? "!ml-auto" : ""
              )}>
              Edit
            </Button>
          )}
        </div>

        <ConfirmationModal
          toggleConfirmModal={toggleSendInvoiceConfirmModal}
          isConfirmModalOpen={isSendInvoiceConfirmModalOpen}
          content="Please make sure that any attached documents that are required for invoicing have completed processing for upload before confirming to send."
          labelOKButton="Save"
          onOK={sendInvoice}
        />

        <ConfirmationModal
          toggleConfirmModal={toggleDeleteConfirmModal}
          isConfirmModalOpen={isDeleteConfirmModalOpen}
          content="Are you sure you want to delete this payment?"
          labelOKButton="Delete"
          onOK={deleteFR}
        />
      </form>
    </FormProvider>
  );
}
